import { useForm } from "react-hook-form";
import { PapInput, PapErrors } from '../components/FormElements';
import { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { motion } from 'framer-motion';
import { UserPlus } from 'lucide-react';
import useApi from "../hooks/useApi";
import SocialLogin from "../components/Social/SocialLogin";
import AuthLayout from '../components/AuthLayout';

interface RegisterFormData {
  email: string;
  full_name: string;
  password: string;
  password_confirmation: string;
}

const schema = yup.object().shape({
  email: yup.string().email('Please enter a valid email').required('Email is required'),
  full_name: yup.string().required('Full name is required'),
  password: yup.string().required('Password is required').min(6, 'Password must be at least 6 characters'),
  password_confirmation: yup.string().required('Please confirm your password').min(6, 'Password must be at least 6 characters').oneOf([yup.ref('password')], 'Passwords must match')
});

const stagger = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.15 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.23, 1, 0.32, 1] as const } },
};

const Page: React.FC = () => {
  const history = useHistory();
  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const api = useApi();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<RegisterFormData>({
    resolver: yupResolver(schema)
  });

  const registerUser = async (data: RegisterFormData) => {
    try {
      const result = await api.post('/api/v1/users', data);
      if (result) {
        history.push('/login');
      }
    } catch (ex: any) {
      if (ex.response) {
        const exResponseBody = await ex.response.json();
        setPageErrors([exResponseBody.error]);
      }
    }
  };

  return (
    <AuthLayout>
      <motion.div variants={stagger} initial="hidden" animate="show">
        {/* Logo & tagline */}
        <motion.div variants={fadeUp} className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-foreground mb-1.5">
            Plan-A-Play
          </h1>
          <p className="text-sm text-muted-foreground">Create your account</p>
        </motion.div>

        {/* Social login */}
        <motion.div variants={fadeUp} className="mb-6">
          <SocialLogin />
        </motion.div>

        {/* Divider */}
        <motion.div variants={fadeUp} className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border/60" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-card/90 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              or register with email
            </span>
          </div>
        </motion.div>

        {/* Errors */}
        {pageErrors.length > 0 && (
          <motion.div variants={fadeUp}>
            <PapErrors errors={pageErrors} />
          </motion.div>
        )}

        {/* Form */}
        <motion.form variants={fadeUp} noValidate onSubmit={handleSubmit(registerUser)} method="post">
          <PapInput fieldLabel="Full Name" formBinding={register("full_name")} placeholder="" fieldName="full_name" error={errors.full_name} required={true} />
          <PapInput fieldLabel="Email" type="email" formBinding={register("email")} placeholder="" fieldName="email" error={errors.email} required={true} />
          <PapInput type="password" fieldLabel="Password" formBinding={register("password")} placeholder="" fieldName="password" error={errors.password} required={true} />
          <PapInput type="password" fieldLabel="Confirm Password" formBinding={register("password_confirmation")} placeholder="" fieldName="password_confirmation" error={errors.password_confirmation} required={true} />

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary btn-block py-3 text-base font-semibold gap-2 disabled:opacity-50"
          >
            {isSubmitting ? (
              <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
            ) : (
              <UserPlus className="w-4 h-4" />
            )}
            {isSubmitting ? 'Creating account...' : 'Create Account'}
          </button>

          <p className="text-xs text-center mt-4 text-muted-foreground">
            By clicking Create Account, you agree to Plan-A-Play&apos;s{' '}
            <Link to="/toc" className="text-accent hover:text-accent/80 hover:underline font-medium transition-colors">Terms and Conditions</Link>.
          </p>
        </motion.form>

        {/* Login link */}
        <motion.p variants={fadeUp} className="text-center text-sm text-muted-foreground mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-accent hover:text-accent/80 hover:underline font-semibold transition-colors">
            Sign in
          </Link>
        </motion.p>
      </motion.div>
    </AuthLayout>
  );
};

export default Page;
